import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { EASE } from "@/components/Reveal";
import { SiteFooter } from "@/components/SiteFooter";

const navCls =
  "font-garamond text-lg text-white/90 transition-colors duration-300 hover:text-white";

export default function Landing() {
  return (
    <div data-testid="landing-page" className="relative min-h-screen">
      <div className="fixed inset-0" aria-hidden="true">
        <motion.img
          src="/assets/coast.webp"
          alt=""
          initial={{ scale: 1.06 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2.8, ease: EASE }}
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/10 to-transparent" />
      </div>

      <div className="relative">
        <header
          data-testid="site-header"
          className="flex items-center justify-between px-6 py-7 sm:px-12"
        >
          <span
            data-testid="header-wordmark"
            className="font-garamond text-2xl text-white"
          >
            syndeō
          </span>
          <nav className="flex items-center gap-8 sm:gap-12">
            <Link to="/apply" data-testid="nav-members" className={navCls}>
              Members
            </Link>
            <Link
              to="/professionals"
              data-testid="nav-recommend"
              className={navCls}
            >
              Professionals
            </Link>
          </nav>
        </header>

        <section className="flex min-h-[58vh] flex-col items-center px-6 pt-16 sm:pt-24">
          <div>
            <motion.h1
              data-testid="hero-wordmark"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.2, ease: EASE }}
              className="text-left font-garamond text-6xl leading-none text-white [text-shadow:0_1px_16px_rgba(0,0,0,0.3)] sm:text-7xl"
            >
              syndeō
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.2, delay: 0.25, ease: EASE }}
              className="mt-8 max-w-md text-left font-garamond text-xl leading-relaxed text-white [text-shadow:0_1px_12px_rgba(0,0,0,0.35)] sm:text-2xl"
            >
              Syndeō is a private platform for discovering exceptional
              professionals and service providers, informed by firsthand
              experience and trusted recommendations.
            </motion.p>
          </div>
        </section>

        <motion.section
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, delay: 0.5, ease: EASE }}
          className="mx-4 border border-stone/50 bg-chalk sm:mx-10 lg:mx-auto lg:max-w-5xl"
        >
          <div className="grid gap-px bg-stone/40 md:grid-cols-2">
            <Link
              to="/apply"
              data-testid="door-apply"
              className="group bg-chalk p-10 transition-colors duration-500 hover:bg-paper sm:p-12"
            >
              <p className="font-oswald text-[11px] uppercase tracking-[0.35em] text-seaglass">
                Membership
              </p>
              <h3 className="mt-4 font-garamond text-3xl text-ink">
                Request Access
              </h3>
              <p className="mt-4 font-garamond text-base leading-relaxed text-softblack/80">
                Discover trusted professionals and exceptional expertise,
                informed by firsthand recommendations and research.
              </p>
              <span className="mt-8 inline-flex items-center gap-2 font-oswald text-xs uppercase tracking-[0.3em] text-ink">
                Begin
                <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
              </span>
            </Link>
            <Link
              to="/professionals"
              data-testid="door-recommend"
              className="group bg-chalk p-10 transition-colors duration-500 hover:bg-paper sm:p-12"
            >
              <p className="font-oswald text-[11px] uppercase tracking-[0.35em] text-seaglass">
                Recommendations
              </p>
              <h3 className="mt-4 font-garamond text-3xl text-ink">
                Professional Submission
              </h3>
              <p className="mt-4 font-garamond text-base leading-relaxed text-softblack/80">
                Exceptional professionals may be recommended, invited, or
                submit themselves for consideration.
              </p>
              <span className="mt-8 inline-flex items-center gap-2 font-oswald text-xs uppercase tracking-[0.3em] text-ink">
                Submit
                <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
              </span>
            </Link>
          </div>
          <SiteFooter />
        </motion.section>

        <div className="h-10 sm:h-16" />
      </div>
    </div>
  );
}
